import { navigate } from "gatsby";
import React, { FC, useEffect, useRef } from "react";

import { useAuthContext } from "../../../auth";
import { Container } from "../../../components/container";
import { PersonalAreaProfileFormSection } from "../../../components/forms/personal-area-profile-form/form-section";
import { PersonalAreaTabList } from "../../../components/personal-area/personal-area-tab-list";
import { Spinner } from "../../../components/spinner";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { getLocalizedPath } from "../../../utils/pages";

interface Props {
  labels: Record<string, string>;
  locale: string;
}

export const ProfilePageBody: FC<Props> = ({ labels, locale }) => {
  const { logged, checking, userInfo } = useAuthContext();
  // Redirect to homepage if i'm not logged or a corporate account
  useEffect(() => {
    !checking && (!logged || (logged && userInfo?.idp)) && navigate(getLocalizedPath("/", locale));
  }, [checking, logged, locale, userInfo?.idp]);

  // Send an event the first time the page is viewed (and only first time)
  const { pushEvent } = useDataLayer();
  const hasSentViewEvent = useRef(false);
  useEffect(() => {
    if (!checking && logged && !userInfo?.idp && !hasSentViewEvent.current) {
      pushEvent({
        event: GoogleAnalyticsEvent.ViewPersonalAreaProfile,
      });
      hasSentViewEvent.current = true;
    }
  }, [pushEvent, checking, logged, userInfo]);

  return !logged ? (
    <div className="flex justify-center items-center h-screen">
      <Spinner size="large" className="m-auto" />
    </div>
  ) : (
    <Container pagePaddingX>
      <div className="flex flex-col items-center mt-5">
        <PersonalAreaTabList currentPage="profile" node_locale={locale} className="max-w-5xl" />
        <div className="flex flex-col py-16 gap-11">
          <div className="text-center flex flex-col gap-8 items-center">
            <div className="text-primary text-5xl lg:text-7xl font-medium leading-tight">
              {labels["personal-area-profile-title"]}
            </div>
            <div className="text-base lg:text-lg leading-snug font-light">
              {labels["personal-area-profile-abstract"]}
            </div>
          </div>
          <PersonalAreaProfileFormSection lang={locale} />
        </div>
      </div>
    </Container>
  );
};
